import React, { useState, useMemo } from 'react';
import { ChevronLeft } from 'lucide-react';
import { InputGroup } from '../common/InputGroup';
import { DropdownTypeahead } from '../common/DropdownTypeahead';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { City } from '../../types/profile';
import { useProfileStore } from '../../stores/profileStore';
import { toast } from 'react-toastify';
import type { Profile } from '../../types/profile';
import { useCitySelector } from '../../hooks/useCitySelector';

export interface EditFreelancerInfoProps {
  profile: Profile;
  onSave?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip?: () => void;
}

const EditFreelancerInfo: React.FC<EditFreelancerInfoProps> = ({
  profile,
  onSave,
  onNext,
  onPrevious,
  onSkip,
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const { updateProfileInfo } = useProfileStore();
  const [firstName, setFirstName] = useState(profile?.first_name || '');
  const [lastName, setLastName] = useState(profile?.last_name || '');
  const [postalCode, setPostalCode] = useState(profile?.postal_code || '');
  const [phone, setPhone] = useState(profile?.phone || '');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [saving, setSaving] = useState(false);

  const {
    selectedOptions: selectedCities,
    setSelectedOptions: setSelectedCities,
    options: cityOptions,
  } = useCitySelector(profile?.city ? [profile.city] : []);

  const selectedCity = selectedCities[0];

  const setSelectedCity = (city: City | null) => {
    setSelectedCities(city ? [city] : []);
  };

  const isEdited = useMemo(() => {
    return (
      (profile?.first_name || '') !== firstName ||
      (profile?.last_name || '') !== lastName ||
      (profile?.postal_code || '') !== postalCode ||
      (profile?.phone || '') !== phone ||
      (profile?.city?.id || null) !== (selectedCity?.id || null)
    );
  }, [firstName, lastName, postalCode, phone, selectedCity, profile]);

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!firstName.trim()) newErrors.firstName = t('fieldRequired');
    if (!lastName.trim()) newErrors.lastName = t('fieldRequired');
    if (!selectedCity) newErrors.city = t('fieldRequired');
    if (!phone.trim()) newErrors.phone = t('fieldRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !profile?.id) return;

    setSaving(true);
    try {
      await updateProfileInfo({
        userId: profile.id,
        firstName,
        lastName,
        postalCode,
        bio: profile.bio,
        cityId: selectedCity?.id || null,
        languages: profile.languages!,
        phone,
      });

      toast.success(t('profileUpdateSuccess'));
      if (onNext) {
        onNext();
      } else if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error('Error updating profile info:', err);
      toast.error(t('profileUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-8 mt-8">
        {/* Form section */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
          <InputGroup
            label={t('firstName')}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder={t('firstNamePlaceholder')}
            required
            error={!!errors.firstName}
            errorMessage={errors.firstName}
          />
          <InputGroup
            label={t('lastName')}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            placeholder={t('lastNamePlaceholder')}
            required
            error={!!errors.lastName}
            errorMessage={errors.lastName}
          />
          <InputGroup label={t('city')} required error={!!errors.city} errorMessage={errors.city}>
            <DropdownTypeahead
              className="mt-2"
              value={profile?.city?.name || ''}
              onChange={(_val: string, item?: City) => {
                setSelectedCity(item ?? null);
                if (errors.city) setErrors(prev => ({ ...prev, city: undefined }));
              }}
              options={cityOptions}
              getOptionLabel={item => item.name}
              getOptionValue={item => item.id}
              placeholder={t('cityPlaceholder')}
              showDropdownIcon={false}
              error={!!errors.city}
            />
          </InputGroup>
          <InputGroup
            label={t('postalCode')}
            value={postalCode}
            onChange={e => setPostalCode(e.target.value)}
            placeholder={t('postalCodePlaceholder')}
            error={!!errors.postalCode}
            errorMessage={errors.postalCode}
          />
          <InputGroup
            label={t('phone')}
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder={t('phonePlaceholder')}
            required
            error={!!errors.phone}
            errorMessage={errors.phone}
          />
        </div>
      </div>
      <div className="flex flex-col gap-4 md:flex-row-reverse mt-8">
        <Button
          variant="primary"
          onClick={handleSave}
          className="w-full md:w-auto px-10 py-1"
          disabled={(!isEdited || saving) && !onSave && !onNext}
        >
          {saving
            ? t('common:form_actions.saving', { ns: 'common' })
            : onNext
              ? t('common:form_actions.next', { ns: 'common' })
              : t('improveProfileStep.save')}
        </Button>
        {onSkip && (
          <Button
            variant="transparent-with-border"
            onClick={onSkip}
            className="w-full md:w-auto px-10 py-1"
          >
            {t('common:form_actions.skip', { ns: 'common' })}
          </Button>
        )}
        {onPrevious && (
          <Button
            variant="simple-text"
            onClick={onPrevious}
            className="w-full md:w-auto px-10 py-1 flex items-center gap-2"
          >
            <ChevronLeft size={20} aria-hidden />
            {t('common:form_actions.previous', { ns: 'common' })}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EditFreelancerInfo;
